/**
 * Agent portal — read-only "what am I getting" report.
 *
 * Backed by GET /v1/agent/commissions?from=&to=. Gated on the backend
 * by RequireAgent (JWT must carry role='agent'); the agent portal
 * routes use RequireAgentRoute on the frontend so a regular user can't
 * navigate here even with a guessed URL.
 *
 * The response always includes period_start, period_end, total_accrued
 * across resellers, plus a per-reseller breakdown sorted by accrued
 * descending. An agent attached to multiple resellers sees one row per.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { AgentBasis } from '@/hooks/useAdminAgents';

export interface AgentAccruedItem {
  commission_id: number;
  reseller_id: number;
  reseller_name: string;
  percentage: number;
  basis: AgentBasis;
  gross_revenue: number;
  net_margin: number;
  accrued: number;
}

export interface MyCommissionsResponse {
  period_start: string;
  period_end: string;
  total_accrued: number;
  items: AgentAccruedItem[];
}

const KEY = (from: string, to: string) => ['agent', 'commissions', from, to] as const;

export function useMyCommissions(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: KEY(from, to),
    queryFn: () =>
      api.get<MyCommissionsResponse>(
        `/v1/agent/commissions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: enabled && !!from && !!to,
    staleTime: 30_000,
  });
}
